<script setup lang="ts">
import { RouterLink } from 'vue-router';

import BrandLogo from '#/components/layout/BrandLogo.vue';
import { useScrollTo } from '#/composables/useScrollTo';
import { brand } from '#/config/brand';
import { FOOTER_COLUMNS, FOOTER_LEGAL_LINKS, isRouteLink } from '#/config/footerLinks';

/**
 * WorkspaceFooter — 2차 AI 입시분석 워크스페이스 푸터(Figma 랜딩 2026-09, 라이트 톤).
 *
 * 커머스 AppFooter(다크 그린)와 **링크·사업자 정보는 같은 SSoT**(config/footerLinks.ts · config/brand.ts)를
 * 쓰고 시각만 다르다 — 흰 면 + 상단 그림자, 브랜드 로고(기존 파일, BrandLogo 기본 변형) + 소개문 좌측,
 * 링크 3열 우측, 사업자 정보·법적 링크·저작권 하단. WorkspaceLayout 이 footer slot 으로 주입한다.
 */
const { goToSection } = useScrollTo();
</script>

<template>
  <footer class="wf">
    <div class="wrap wf-in">
      <div class="wf-top">
        <div class="wf-brand">
          <BrandLogo />
          <p class="wf-desc">
            30년간 대학 합격 점수를 매년 분석한
            <span class="whitespace-nowrap">입시 데이터 분석전문가</span>와
            <span class="whitespace-nowrap">대학 입학사정관 출신의</span>{{ ' ' }}
            <span class="whitespace-nowrap">대치동 입시 전문가</span>가
            <span class="whitespace-nowrap">전국 120여 개 대학의</span> 학과 및 전형별
            <span class="whitespace-nowrap">지원 전략과</span>{{ ' ' }}
            <span class="whitespace-nowrap">예상 합격선을</span> 직접 분석했습니다.
          </p>
        </div>

        <nav class="wf-cols" aria-label="푸터 링크">
          <div v-for="col in FOOTER_COLUMNS" :key="col.title" class="wf-col">
            <p class="wf-col-title">{{ col.title }}</p>
            <ul class="wf-col-list">
              <li v-for="link in col.links" :key="link.label">
                <RouterLink v-if="isRouteLink(link)" :to="link.to" class="wf-link">
                  {{ link.label }}
                </RouterLink>
                <button v-else type="button" class="wf-link" @click="goToSection('/', link.hash)">
                  {{ link.label }}
                </button>
              </li>
            </ul>
          </div>
        </nav>
      </div>

      <div class="wf-legal">
        <p class="wf-biz">
          <b>상호명</b> {{ brand.legal.company }} &nbsp;|&nbsp; <b>대표자</b>
          {{ brand.legal.ceo }} &nbsp;|&nbsp; <b>사업자등록번호</b> {{ brand.legal.bizNo }}<br />
          <b>통신판매업번호</b> {{ brand.legal.mailOrderNo }} &nbsp;|&nbsp; <b>출판사신고번호</b>
          {{ brand.legal.publisherNo }}<br />
          <b>주소</b> {{ brand.legal.address }} &nbsp;|&nbsp; <b>TEL</b>
          {{ brand.legal.tel }} &nbsp;|&nbsp; <b>이메일</b> {{ brand.legal.email }}
        </p>
        <div class="wf-legal-links">
          <template v-for="(link, i) in FOOTER_LEGAL_LINKS" :key="link.to">
            <RouterLink :to="link.to" class="wf-legal-link">{{ link.label }}</RouterLink>
            <span v-if="i < FOOTER_LEGAL_LINKS.length - 1" aria-hidden="true">·</span>
          </template>
        </div>
        <p class="wf-copy">{{ brand.legal.copyright }}</p>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.wrap {
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 18px;
}

@media (width >= 600px) {
  .wrap {
    padding: 0 32px;
  }
}

@media (width >= 1080px) {
  .wrap {
    padding: 0 40px;
  }
}

/* 기본 = 모바일(Figma Mobile: 패딩 32·본문 12px — 원안 10px 은 가독성 하한으로 12 채택), md 부터 14px */
.wf {
  background: var(--ws-surface);

  /* 상단 그림자(Figma 0 -2px 2px) — `--ws-shadow-bar` 의 상향 변형 */
  box-shadow: 0 -2px 2px rgb(0 0 0 / 5%);
  color: var(--ws-muted);
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.wf-in {
  padding-top: 32px;
  padding-bottom: 32px;
}

@media (width >= 600px) {
  .wf {
    font-size: 14px;
    line-height: 24px;
  }

  .wf-in {
    padding-top: 36px;
  }
}

.wf-top {
  display: grid;
  gap: 28px;
}

/* 태블릿(≥768)부터 데스크톱 구성(브랜드 | 링크 3열) */
@media (width >= 768px) {
  .wf-top {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 32px;
  }
}

@media (width >= 980px) {
  .wf-top {
    gap: 40px;
  }
}

.wf-brand {
  max-width: 420px;
}

.wf-desc {
  margin: 20px 0 0;
  word-break: keep-all;
}

@media (width >= 980px) {
  .wf-desc {
    margin-top: 28px;
  }
}

/* 링크 3열 — Figma Mobile 에는 없다(브랜드·사업자 정보만). md 부터 노출 */
.wf-cols {
  display: none;
  grid-template-columns: repeat(3, auto);
  gap: 24px 48px;
}

@media (width >= 600px) {
  .wf-cols {
    display: grid;
  }
}

@media (width >= 980px) {
  .wf-cols {
    column-gap: 40px;
    padding-top: 6px;
  }
}

@media (width >= 1280px) {
  .wf-col {
    min-width: 88px;
  }
}

.wf-col-title {
  margin: 0 0 9px;
  font-weight: 700;
  color: var(--ws-muted);
}

.wf-col-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.wf-col-list li + li {
  margin-top: 5px;
}

.wf-link {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition: color 0.15s;
  white-space: nowrap;
}

.wf-link:hover {
  color: var(--ws-green);
}

.wf-legal {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid var(--ws-line);
}

@media (width >= 980px) {
  .wf-legal {
    margin-top: 44px;
    padding-top: 0;
    border-top: 0;
  }
}

.wf-biz {
  margin: 0;
  max-width: 640px;
  word-break: keep-all;
}

.wf-biz b {
  font-weight: 700;
}

.wf-legal-links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
}

.wf-legal-link {
  text-decoration: underline;
  text-underline-offset: 3px;
  color: inherit;
}

.wf-legal-link:hover {
  color: var(--ws-green);
}

.wf-copy {
  margin: 4px 0 0;
}
</style>
